<script setup lang="ts">
  import { useEventsBus } from '@haskoning/vue-component-library'
  import type { SectionSelectionPolygon } from '@/stores/type/selectionPolygon.type'
  import type { GroundInvestigationPoint } from '@/stores/type/giPoint.type'
  import { findSelectionGIPointsIDs } from '@/utils/map'
  import useProfilePermissions from '@/composables/useProfilePermission'
  import type { SectionLine } from '@/stores/type/sectionLine.type'
  import SecondaryButton from '@/components/shared/SecondaryButton.vue'

  const { emit: busEmit } = useEventsBus()
  const projectStore = useProjectStore()
  const sectionLineUploadCollectionsStore = useSectionLineUploadCollectionsStore()
  const sectionLinesStore = useSectionLinesStore()
  const selectionPolygonsStore = useSelectionPolygonsStores()
  const giPointsStore = useGIPointsStore()
  const sectionLinesGIPointsStore = useSectionLinesGIPointsStore()

  const { items: giPoints } = storeToRefs(giPointsStore)
  const {
    selectedItems: selectedSectionLineCollections,
    isMultipleSelection: isMultipleCollectionSelection,
    isSingleSelection: isSingleCollectionSelection
  } = storeToRefs(sectionLineUploadCollectionsStore)

  const { selectedItems: selectedSectionLines, isSingleSelection: isSingleSectionLineSelection } =
    storeToRefs(sectionLinesStore)

  const { giPointsBySectionLineId } = storeToRefs(sectionLinesGIPointsStore)

  const { polygonsBySectionLineId } = storeToRefs(selectionPolygonsStore)
  const {
    canUploadPolygon,
    canAddSectionLine,
    canAddSectionLineGIPoint,
    canDeleteSectionLineUploadCollection,
    canRemoveSectionLineGIPoint,
    canDeleteSectionLine
  } = useProfilePermissions()
  const bufferZoneSize = ref(50)
  const rules = {
    required: (value: string) => !!value || 'Required.',
    number: (value: string) => !isNaN(Number(value)) || 'Must be a number.'
  }

  const addSelectionByPolygons = async (
    sectionLine: SectionLine,
    polygons: SectionSelectionPolygon[],
    giPointsArr: GroundInvestigationPoint[]
  ) => {
    if (giPointsBySectionLineId.value(sectionLine.id).length > 0) {
      busEmit('error', {
        status: 400,
        statusText: 'Please remove the existing GI Points of this Section Line.',
        data: '',
        method: 'POST'
      })
      return
    }
    const selectedGIPointsIDs = findSelectionGIPointsIDs(polygons, giPointsArr)
    if (selectedGIPointsIDs.length > 0) {
      await sectionLinesGIPointsStore.addGIPointsToSectionLine(selectedGIPointsIDs, sectionLine.id)
    }
  }
  const generateBufferZone = async (sectionLines: SectionLine[], bufferZoneWidth: number) => {
    for (const sectionLine of sectionLines) {
      const bufferZone = await sectionLinesStore.generateBufferZone(sectionLine.id, bufferZoneWidth)
      selectionPolygonsStore.removePolygonsBySectionLineID(sectionLine.id)
      sectionLinesGIPointsStore.removeSelectionsFromSectionLine(sectionLine.id)
      selectionPolygonsStore.items.push(bufferZone)
      addSelectionByPolygons(
        sectionLine,
        polygonsBySectionLineId.value(sectionLine.id),
        giPoints.value
      )
    }
  }
</script>

<template>
  <div
    class="d-flex justify-start align-center bg-white flex-wrap ga-2 pt-2 px-2 pb-2"
    style="top: 0; position: sticky; z-index: 1000"
  >
    <SecondaryButton
      v-if="canAddSectionLine"
      prepend-icon="mdi-pen"
      :disabled="selectedSectionLineCollections.length === 0 || isMultipleCollectionSelection"
      @click="busEmit('draw-section-line')"
    >
      Draw Section Line
    </SecondaryButton>
    <v-menu>
      <template v-slot:activator="{ props }">
        <SecondaryButton
          v-if="canUploadPolygon"
          prepend-icon="mdi-map-marker"
          append-icon="mdi-menu-down"
          :disabled="selectedSectionLines.length === 0"
          v-bind="props"
        >
          Add GI Data
        </SecondaryButton>
      </template>
      <v-list class="py-0">
        <v-list-item
          v-show="canUploadPolygon && isSingleCollectionSelection"
          @click="sectionLinesStore.toggleAddPolygonShapeFileDialog()"
        >
          <v-list-item-title> Upload Polygon Shape File</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="canUploadPolygon && isSingleSectionLineSelection"
          density="compact"
          v-ripple
          @click="
            sectionLinesStore.setCurrentItem(selectedSectionLines[0]), busEmit('draw-polygon')
          "
        >
          <v-list-item-title class="cursor-pointer">Draw A Polygon</v-list-item-title>
        </v-list-item>
        <v-list-item
          density="compact"
          v-ripple
          v-if="canUploadPolygon && selectedSectionLines.length > 0"
        >
          <v-list-item-title class="d-flex align-center g-2 justify-center" density="compact">
            Buffer Zone
            <v-text-field
              v-model="bufferZoneSize"
              color="primary"
              density="compact"
              suffix="meters"
              style="height: 40px"
              :rules="[rules.required, rules.number]"
              @click.stop=""
            >
            </v-text-field>
            <v-btn
              density="comfortable"
              color="primary"
              variant="flat"
              @click="generateBufferZone(selectedSectionLines, bufferZoneSize)"
              >Apply
            </v-btn>
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="canAddSectionLineGIPoint && isSingleSectionLineSelection"
          density="compact"
          v-ripple
          @click="
            projectStore.toggleDrawerType('groundInvestigationDrawer'),
              sectionLinesStore.toggleSelectionOption(selectedSectionLines[0].id, 'create')
          "
        >
          <v-list-item-title v-ripple class="cursor-pointer">Add Manually</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <SecondaryButton
      v-if="canRemoveSectionLineGIPoint"
      :disabled="selectedSectionLines.length === 0"
      prepend-icon="mdi-refresh"
    >
      Reselect
    </SecondaryButton>
    <v-menu>
      <template v-slot:activator="{ props }">
        <SecondaryButton
          v-if="canDeleteSectionLineUploadCollection || canDeleteSectionLine"
          prepend-icon="mdi-delete"
          append-icon="mdi-menu-down"
          v-bind="props"
          :disabled="
            selectedSectionLines.length === 0 && selectedSectionLineCollections.length === 0
          "
        >
          Delete
        </SecondaryButton>
      </template>
      <v-list class="py-0">
        <v-list-item
          v-if="canDeleteSectionLineUploadCollection && selectedSectionLineCollections.length > 0"
          density="compact"
          v-ripple
          @click="sectionLineUploadCollectionsStore.toggleDeleteDialog()"
        >
          <v-list-item-title class="cursor-pointer">Delete Selected Datasets</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="canDeleteSectionLine && selectedSectionLines.length > 0"
          density="compact"
          v-ripple
          @click="sectionLinesStore.toggleDeleteDialog()"
        >
          <v-list-item-title v-ripple class="cursor-pointer"
            >Delete Selected Section Lines
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="canRemoveSectionLineGIPoint && isSingleSectionLineSelection"
          density="compact"
          v-ripple
          @click="sectionLinesStore.toggleSelectionOption(selectedSectionLines[0].id, 'remove')"
        >
          <v-list-item-title v-ripple class="cursor-pointer"
            >Remove GI Point From Section Line
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<style scoped></style>
